import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
} from '@coreui/react'
import { useNavigate } from 'react-router-dom'
import WidgetSum from '../widgets/WidgetSum'
import WidgetStatisticAreaSum from '../widgets/WidgetStatisticAreaSum'
import WidgetRemindersSum from '../widgets/WidgetRemindersSum'
import { AOI_FULL_CONFIG, getEnabledAoiMap, profileModules } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'

const Yearlyclosure = () => {
  const { t } = useTranslation()
  const enabledModules = useMemo(() => getEnabledAoiMap(), [])

  const moduleMeta = useMemo(
    () =>
      AOI_FULL_CONFIG.map(({ key, grp_ID, img }) => ({
        key,
        grp_ID,
        pic: img,
      })),
    [],
  )

  const categorizedModules = useMemo(() => {
    const categories = {
      personal: [],
      business: [],
      other: [],
    }

    moduleMeta.forEach((meta) => {
      if (!enabledModules[meta.key]) {
        return
      }

      if (profileModules.personal?.includes(meta.key)) {
        categories.personal.push(meta)
        return
      }

      if (profileModules.business?.includes(meta.key)) {
        categories.business.push(meta)
        return
      }

      categories.other.push(meta)
    })

    return categories
  }, [enabledModules, moduleMeta])

  const hasAnyModule = useMemo(
    () =>
      categorizedModules.personal.length > 0 ||
      categorizedModules.business.length > 0 ||
      categorizedModules.other.length > 0,
    [categorizedModules],
  )

  const tabLabels = useMemo(
    () => ({
      personal: t('statistics.areas.tabs.personal'),
      business: t('statistics.areas.tabs.business'),
      other: t('statistics.areas.tabs.other'),
    }),
    [t],
  )

  const availableAoITabs = useMemo(() => {
    const tabs = []
    if (categorizedModules.personal.length > 0) {
      tabs.push({ key: 'personal', label: tabLabels.personal })
    }
    if (categorizedModules.business.length > 0) {
      tabs.push({ key: 'business', label: tabLabels.business })
    }
    if (categorizedModules.other.length > 0) {
      tabs.push({ key: 'other', label: tabLabels.other })
    }
    return tabs
  }, [categorizedModules, tabLabels])

  const [activeAoITab, setActiveAoITab] = useState(() => availableAoITabs[0]?.key ?? 'personal')

  useEffect(() => {
    if (!availableAoITabs.find((tab) => tab.key === activeAoITab)) {
      setActiveAoITab(availableAoITabs[0]?.key ?? 'personal')
    }
  }, [availableAoITabs, activeAoITab])

  const navigate = useNavigate()
  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('statistics.header.title')}
          onBack={() => navigate('/dashboard')}
        />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 small text-secondary d-flex flex-column gap-4">
                <CRow className="gy-4 gx-0 gx-lg-4 align-items-start">
                  <CCol lg={5} className="d-flex flex-column gap-3">
                    <section>
                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                        {t('statistics.summary.title')}
                      </h6>
                      <WidgetSum />
                    </section>
                    <section className="reminders-summary-limit mx-auto w-100">
                      <WidgetRemindersSum uniqueKey={1} />
                    </section>
                  </CCol>
                  <CCol lg={7} className="d-flex flex-column gap-3">
                    <section className="d-flex flex-column gap-3">
                      <h6 className="text-uppercase text-muted fw-semibold mb-2">
                        {t('statistics.areas.title')}
                      </h6>
                      {hasAnyModule ? (
                        <>
                          {availableAoITabs.length > 1 && (
                            <CButtonGroup role="tablist" className="flex-wrap">
                              {availableAoITabs.map((tab) => (
                                <CButton
                                  key={tab.key}
                                  type="button"
                                  color={activeAoITab === tab.key ? 'primary' : 'secondary'}
                                  {...(activeAoITab === tab.key ? {} : { variant: 'outline' })}
                                  size="sm"
                                  className={`text-uppercase fw-semibold yearlyclosure-tab ${
                                    activeAoITab === tab.key
                                      ? 'yearlyclosure-tab--active'
                                      : 'yearlyclosure-tab--inactive'
                                  }`}
                                  onClick={() => setActiveAoITab(tab.key)}
                                  role="tab"
                                  aria-selected={activeAoITab === tab.key}
                                >
                                  {tab.label}
                                </CButton>
                              ))}
                            </CButtonGroup>
                          )}
                          <CRow className="row-cols-1 row-cols-md-2 g-2" role="tabpanel">
                            {(categorizedModules[activeAoITab] ?? []).map(
                              ({ key, grp_ID, pic }) => (
                                <CCol key={key} className="mb-2">
                                  <WidgetStatisticAreaSum grp_ID={grp_ID} pic={pic} />
                                </CCol>
                              ),
                            )}
                          </CRow>
                        </>
                      ) : (
                        <CAlert color="info" className="mb-0">
                          {t('statistics.areas.empty')}{' '}
                          <CButton
                            color="info"
                            size="sm"
                            className="ms-2"
                            onClick={() => navigate('/settings')}
                          >
                            {t('statistics.areas.goToSettings')}
                          </CButton>
                        </CAlert>
                      )}
                    </section>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Yearlyclosure
